/**
 * School Partner Dashboard Home — stats and quick actions only (no recent payments / quick links blocks).
 */
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl, getAuthToken } from '../../services/api';
import '../tabs/AgentOverview.css';

const API_BASE_URL = getApiBaseUrl();

const SchoolOverview = ({ onTabChange }) => {
  const { t } = useTranslation(['common']);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    paymentsSubmittedToday: 0,
    pendingPayments: 0,
    approvedPaymentsToday: 0,
    studentsEnrolled: 0
  });
  const isMountedRef = useRef(true);

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const displayName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim()
    || user.name || user.email || 'Partner';

  useEffect(() => {
    isMountedRef.current = true;
    fetchSchoolStats();
    const interval = setInterval(() => {
      if (isMountedRef.current) fetchSchoolStats();
    }, 30000);
    return () => {
      isMountedRef.current = false;
      clearInterval(interval);
    };
  }, []);

  const fetchSchoolStats = async () => {
    try {
      const token = getAuthToken();
      const res = await fetch(`${API_BASE_URL}/api/school/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      if (res.ok) {
        const data = await res.json();
        if (data.success && data.stats && isMountedRef.current) {
          setStats(data.stats);
        }
      }
    } catch (e) {
      console.error('School dashboard stats error:', e);
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="agent-overview school-overview">
        <p>{t('common:school.loadingDashboard')}</p>
      </div>
    );
  }

  return (
    <div className="agent-overview school-overview">
      <div className="overview-header">
        <div className="header-left">
          <h2>{t('common:school.welcomeBack', { name: displayName })}</h2>
          <p className="role-badge">{t('common:school.partnerRole')}</p>
        </div>
        <div className="header-right">
          <div className="shift-status">
            <span className="status-indicator active"></span>
            <span>{t('common:school.partnerActive')}</span>
          </div>
        </div>
      </div>

      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon payments-submitted">
            <span className="material-icons">payment</span>
          </div>
          <div className="stat-content">
            <h3>{stats.paymentsSubmittedToday}</h3>
            <p>{t('common:school.stats.paymentsSubmittedToday')}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon pending">
            <span className="material-icons">schedule</span>
          </div>
          <div className="stat-content">
            <h3>{stats.pendingPayments}</h3>
            <p>{t('common:school.stats.pendingPayments')}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon approved">
            <span className="material-icons">check_circle</span>
          </div>
          <div className="stat-content">
            <h3>{stats.approvedPaymentsToday}</h3>
            <p>{t('common:school.stats.approvedToday')}</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon students">
            <span className="material-icons">school</span>
          </div>
          <div className="stat-content">
            <h3>{stats.studentsEnrolled}</h3>
            <p>{t('common:school.stats.studentsEnrolled')}</p>
          </div>
        </div>
      </div>

      <div className="quick-actions">
        <h3>{t('common:school.quickActions')}</h3>
        <div className="actions-grid">
          <button
            type="button"
            className="action-card"
            onClick={() => onTabChange && onTabChange('manual-payments')}
          >
            <span className="material-icons">add_circle</span>
            <span>{t('common:school.actions.recordPayment')}</span>
          </button>
          <button
            type="button"
            className="action-card"
            onClick={() => onTabChange && onTabChange('manual-payments')}
          >
            <span className="material-icons">receipt</span>
            <span>{t('common:school.actions.viewMyPayments')}</span>
          </button>
          <button
            type="button"
            className="action-card"
            onClick={() => onTabChange && onTabChange('receipts')}
          >
            <span className="material-icons">print</span>
            <span>{t('common:school.actions.printReceipts')}</span>
          </button>
          <button
            type="button"
            className="action-card"
            onClick={() => onTabChange && onTabChange('enroll-student')}
          >
            <span className="material-icons">person_add</span>
            <span>{t('common:school.actions.enrollStudent')}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default SchoolOverview;
